import { LockedFeatureConfig } from 'blocks/TextOverlayModal/v2';
import {
  DisabledConfig,
  FeatureState,
  VideoTemplateEditorFeatures,
} from './types';

function extractValue(val: any, path: Array<string | number>, prevVal?: any) {
  if (path.length === 0) return val;

  if (!val) return prevVal;

  const [seg, ...restPath] = path;
  const nextVal = val?.[seg];

  return extractValue(nextVal, restPath, val);
}

export function getFeatureState(
  features: VideoTemplateEditorFeatures,
  path: Array<string | number>,
): FeatureState {
  if (!path) return true;
  return extractValue(features, path) ?? true;
}

export function isFeatureHidden(
  features: VideoTemplateEditorFeatures,
  path: Array<string | number>,
) {
  const state = getFeatureState(features, path);
  return state === 'hidden';
}

export function isDisabledConfig(value: unknown): value is DisabledConfig {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as DisabledConfig).disabled === true
  );
}

/**
 * Returns true when a nested sub-feature carries a `{ disabled: true }` config,
 * indicating the control should be rendered but non-interactive.
 */
export function isSubFeatureDisabled(
  features: VideoTemplateEditorFeatures,
  path: Array<string | number>,
): boolean {
  return isDisabledConfig(getFeatureState(features, path));
}

/**
 * Reads the feature-state at `path` and produces the modal-shaped
 * `LockedFeatureConfig` (carrying the DisabledConfig's reason as the tooltip)
 * when the feature is locked, or `false` otherwise. Lets modal-prop builders
 * write `lockedFeatures: { fontFamily: getLockedFeature(features, path) }`
 * without any per-call narrowing or shape conversion.
 */
export function getLockedFeature(
  features: VideoTemplateEditorFeatures | undefined,
  path: Array<string | number>,
): false | LockedFeatureConfig {
  const state = getFeatureState(features ?? {}, path);
  return isDisabledConfig(state) ? { tooltip: state.reason } : false;
}
